import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { AssetType, PresignedUrlAction } from '@amzn/fire-tv-campaign-automation-service';
import { AssetContext } from 'src/contexts/AssetContext';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { getPresignedUrls } from 'src/utils/clients/controlPlane/apiMethods';
import { EMBEDDED_MESSAGE_TYPES, EMBEDDED_URL_PARAMS, DEFAULT_PARENT_ORIGIN } from 'src/constants';
import { AssetStatus } from 'src/types';
import { logger } from 'src/logger';

const ASSET_MIME_TYPES: Record<string, string> = {
  [AssetType.BACKGROUND_IMAGE]: 'image/jpeg',
  [AssetType.LOGO]: 'image/png',
  [AssetType.VIDEO_TRAILER]: 'video/mp4',
};

const ALLOWED_PARENT_ORIGINS = new Set([
  'https://advertising.amazon.com',
  'https://advertising.amazon.co.jp',
  'https://advertising.amazon.co.uk',
  'https://advertising.amazon.de',
  'https://advertising.amazon.fr',
  'https://advertising.amazon.es',
  'https://advertising.amazon.it',
  'https://advertising.amazon.com.au',
  'https://advertising.amazon.ca',
  'https://advertising.amazon.in',
  'https://advertising.amazon.com.br',
  'https://advertising.amazon.com.mx',
]);

const getParentOrigin = (): string => {
  if (typeof window === 'undefined') return DEFAULT_PARENT_ORIGIN;
  const raw = new URLSearchParams(window.location.search).get(EMBEDDED_URL_PARAMS.PARENT_ORIGIN);
  return raw && ALLOWED_PARENT_ORIGINS.has(raw) ? raw : DEFAULT_PARENT_ORIGIN;
};

/**
 * Renderless component that manages the postMessage bridge between Frida
 * (running in an iframe) and the parent Rodeo page. Implements a 3-step
 * handshake: signals readiness, waits for asset request, then transfers
 * assets as ArrayBuffers via zero-copy Transferable Objects.
 *
 * Origin is validated against ALLOWED_PARENT_ORIGINS before any communication.
 */
export const EmbeddedDoneButton = () => {
  const { assetStates } = useContext(AssetContext);
  const client = useMemo(() => getControlPlaneClient(), []);
  const [hasNotified, setHasNotified] = useState(false);
  const requestReceivedRef = useRef(false);

  const parentOrigin = useMemo(() => getParentOrigin(), []);

  const completedAssets = useMemo(
    () =>
      Object.entries(assetStates).filter(
        ([_, state]) => state.key && state.status === AssetStatus.COMPLETED,
      ),
    [assetStates],
  );

  const isReady = completedAssets.length > 0;

  // Notify parent when assets become ready
  useEffect(() => {
    if (isReady && !hasNotified) {
      window.parent.postMessage(
        { type: EMBEDDED_MESSAGE_TYPES.ASSETS_READY_TO_SEND },
        parentOrigin,
      );
      setHasNotified(true);
      logger.info('Embedded mode: Notified parent that assets are ready');

      requestReceivedRef.current = false;
      const timeoutId = setTimeout(() => {
        if (!requestReceivedRef.current) {
          logger.warn('Embedded mode: Parent has not requested assets after 30s');
        }
      }, 30000);
      return () => clearTimeout(timeoutId);
    }
    if (!isReady && hasNotified) {
      setHasNotified(false);
    }
  }, [isReady, hasNotified, parentOrigin]);

  const sendAssets = useCallback(async () => {
    if (!completedAssets.length) return;
    try {
      const keys = completedAssets.map(([_, state]) => state.key!);
      const urls = await getPresignedUrls(client, PresignedUrlAction.GET, keys);

      const assets: Record<string, { data: ArrayBuffer; fileName: string; mimeType: string }> = {};
      const buffers: ArrayBuffer[] = [];

      await Promise.all(
        completedAssets.map(async ([assetType], i) => {
          const response = await fetch(urls[i].presignedUrl);
          if (!response.ok)
            throw new Error(`Failed to fetch asset ${assetType}: ${response.status}`);
          const buffer = await response.arrayBuffer();
          assets[assetType] = {
            data: buffer,
            fileName: urls[i].key.split('/').pop() || urls[i].key,
            mimeType: ASSET_MIME_TYPES[assetType] || 'application/octet-stream',
          };
          buffers.push(buffer);
        }),
      );

      window.parent.postMessage(
        { type: EMBEDDED_MESSAGE_TYPES.ASSETS_READY, assets },
        parentOrigin,
        buffers,
      );
      logger.info('Embedded mode: Assets sent to parent', { assetTypes: Object.keys(assets) });
    } catch (error) {
      logger.error('Embedded mode: Failed to prepare assets', { error });
      window.parent.postMessage(
        {
          type: EMBEDDED_MESSAGE_TYPES.ASSETS_ERROR,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
        parentOrigin,
      );
    }
  }, [completedAssets, client, parentOrigin]);

  // Listen for parent requesting assets
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) return;
      if (event.data?.type === EMBEDDED_MESSAGE_TYPES.REQUEST_ASSETS) {
        requestReceivedRef.current = true;
        sendAssets();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendAssets, parentOrigin]);

  return null;
};
